import { createGlobalStyle, css } from 'styled-components'
import theme from 'theme'
import media from 'media'
import { rgba, darken, lighten } from 'polished'
import { MOBILE_NAV_BAR_HEIGHT, NAV_BAR_HEIGHT } from 'consts'

// would be nice to put these inside GlobalStyle, but styled-components recommends not using @import:
// https://styled-components.com/docs/faqs#note-regarding-css-import-and-createglobalstyle
import '../../node_modules/prismjs/themes/prism-tomorrow.css'

const componentBorderWidth = 2

export const BACKGROUND_COLOR = theme.colors.offWhite

const btnColorSolid = ({ color, hoverColor }) => {
  return css`
    color: ${theme.colors.black};
    background-color: ${color};
    border-width: 0;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);

    &:focus,
    &_focus {
      color: ${theme.colors.black};
      background-color: ${hoverColor};
    }
    &:hover {
      color: ${theme.colors.black};
      background-color: ${hoverColor};
    }
    &_disabled,
    &[disabled],
    fieldset[disabled] & {
      background-color: ${lighten(0.15, rgba(color, 0.8))};
      &:hover,
      &:focus,
      &.focus {
        background-color: ${lighten(0.15, rgba(color, 0.8))};
      }
    }
  `
}

const btnVariantInverse = ({ name, color, hoverColor }) => {
  return css`
    &.button_${name} {
      color: ${color};
      &:hover {
        color: ${hoverColor};
      }
    }
  `
}

const GlobalStyle = createGlobalStyle`
  ${({ isFullScreen }) =>
    isFullScreen &&
    `
    #___gatsby,
    #gatsby-focus-wrapper { 
      height: 100%;
    }
  `}

  .ant-select-item-option-active:not(.ant-select-item-option-disabled) {
    background-color: ${theme.colors.blues[10]}
  }

  html {
    box-sizing: border-box;
    scroll-margin-top: ${MOBILE_NAV_BAR_HEIGHT}px;
  }

  ${media.desktop`
    html {
      scroll-margin-top: ${NAV_BAR_HEIGHT}px;
    }
  `}

  html,
  body {
    margin: 0;
    padding: 0;
    height: 100%;
    background: ${BACKGROUND_COLOR};
  }

  // annoying.
  body {
    min-height: 100%;
    display: flex;
    flex-direction: column;
  }

  *,
  *:before,
  *:after {
    box-sizing: inherit;
  }

  .wrapper {
    display: flex;
    min-height: 100vh;
    flex-direction: column;
  }

  .content {
    flex-grow: 1;
  }

  .app-container {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    flex-grow: 1;
    padding: 40px;

    > * {
      flex-grow: 1;
    }
  }

  .page {
    position: relative;
  }

  /* typography */

  html,
  body {
    font-family: ${theme.fontFamilies.body};
    color: ${theme.colors.textColor};
    letter-spacing: 0.02em;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-weight: ${theme.fontWeights.bold};
    margin: 30px 0 20px;
    color: ${theme.colors.textColor};
    line-height: 1.1;

    &:first-child {
      margin-top: 0;
    }

    .anchor {
      margin-left: -25px;
    }
  }

  h1,
  h2 {
    .anchor {
      margin-top: -3px;
    }
  }

  /*
    Text properties
  */

  pre code {
    background: transparent;
  }

  p,
  ul,
  ol,
  pre {
    margin: 20px 0;
    border: none;
    &:first-child {
      margin-top: 0;
    }
    &:last-child {
      margin-bottom: 0;
    }
  }

  ul > li > ul,
  ul > li > ol,
  ol > li > ul,
  ol > li > ol {
    margin-top: 0;
    margin-bottom: 0;
  }

  a {
    color: ${theme.textColors.link};
    text-decoration: none;
  }

  a:hover {
    text-decoration: none;
    color: ${theme.textColors.linkHover};
  }

  code,
  :not(pre) > code[class*='language-'],
  .inline-code code {
    font-size: 14px;
    padding: 2px 4px;
    border-radius: 0;
    color: ${theme.colors.black};
    background: ${rgba(theme.colors.inputBorderColor, 0.6)};
    font-weight: ${theme.fontWeights.bold};
  }

  /* Everything except headings... */
  p > a > code,
  li > a > code,
  span > a > code,
  div > a > code {
    color: ${theme.textColors.link} !important;
    &:hover {
      text-decoration: none;
      color: ${theme.textColors.linkHover} !important;
    }
  }

  h1 code,
  h2 code,
  h3 code,
  h4 code,
  h5 code,
  h6 code {
    font-size: 0.9em;
  }

  strong {
    font-weight: ${theme.fontWeights.bold};
  }

  /* buttons */

  // Modified version of bootstrap buttons
  .button {
    display: inline-block;
    height: ${theme.button.height}px;
    line-height: ${theme.button.height}px;
    margin-bottom: 0; // For input.button
    padding: ${theme.button.padding};
    font-weight: ${theme.fontWeights.bold};
    font-family: ${theme.fontFamilies.body};
    font-size: ${theme.fontSizes[40]}px;
    text-align: center;
    vertical-align: middle;
    touch-action: manipulation;
    cursor: pointer;
    background-image: none; // Reset unusual Firefox-on-Android default style; see https://github.com/necolas/normalize.css/issues/214
    border: none;
    white-space: nowrap;
    border-radius: 0px;
    transition: 0.2s all;
    user-select: none;
    text-decoration: none !important;

    &:hover,
    &:focus,
    &.focus {
      outline: 0;
      text-decoration: none;
    }

    &:active,
    &.active {
      outline: 0;
      background-image: none;
      position: relative;
      top: 1px;
    }

    &.disabled,
    &[disabled],
    fieldset[disabled] & {
      cursor: not-allowed;
      opacity: 0.55;
      box-shadow: none;
    }

    &a {
      &.disabled,
      fieldset[disabled] & {
        pointer-events: none; // Future-proof disabling of clicks on '<a>' elements
      }
    }

    // icon
    i {
      position: relative;
      top: -1.5px;
      margin-right: 4px;
      vertical-align: middle;
      font-size: 1.2em;

      &:last-child {
        margin-right: 0;
        margin-left: 4px;
      }
    }

    &.button_small i {
      font-size: 16px;
    }
  }

  // Alternate buttons


  .button {
    ${btnColorSolid({
      color: theme.colors.black,
      hoverColor: lighten(0.1, theme.colors.black),
    })}
  }

  .button_success {
    ${btnColorSolid({
      color: theme.colors.green,
      hoverColor: lighten(0.1, theme.colors.green),
    })}
  }

  .button_inverse {
    ${btnColorSolid({ color: 'white', hoverColor: 'white' })}
    color: ${theme.colors.black};
    &:hover {
      color: ${theme.colors.black};
    }

    ${btnVariantInverse({
      name: 'primary',
      color: darken(0.1, theme.colors.blue),
      hoverColor: theme.colors.blue,
    })}
    ${btnVariantInverse({
      name: 'secondary',
      color: darken(0.1, theme.colors.green),
      hoverColor: theme.colors.green,
    })}
    ${btnVariantInverse({
      name: 'cta',
      color: darken(0.1, '#F83877'),
      hoverColor: '#F83877',
    })}
    ${btnVariantInverse({
      name: 'info',
      color: darken(0.1, theme.colors.blue),
      hoverColor: theme.colors.blue,
    })}
    ${btnVariantInverse({
      name: 'success',
      color: darken(0.1, theme.colors.green),
      hoverColor: theme.colors.green,
    })}
    ${btnVariantInverse({
      name: 'warning',
      color: theme.colors.yellow,
      hoverColor: darken(0.1, theme.colors.yellow),
    })}
    ${btnVariantInverse({
      name: 'danger',
      color: darken(0.1, theme.colors.red),
      hoverColor: theme.colors.red,
    })}
  }

  .button_shadow {
    box-shadow: ${theme.componentShadows.normal};
  }

  .button_hover_underline {
    &:hover {
      text-decoration: underline;
    }
  }

  // Button Sizes

  .button_huge {
    height: ${theme.button.heightHuge}px;
    line-height: ${theme.button.heightHuge}px;
    padding: ${theme.button.paddingHuge};
    font-size: ${theme.fontSizes[50]}px;

    &.button_outline {
      line-height: ${theme.button.heightHuge - componentBorderWidth * 2}px;
    }
  }
`

export default GlobalStyle
